import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'

const LanguageContext = createContext()

export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en')
  const [translations, setTranslations] = useState({})
  const [isLoading, setIsLoading] = useState(true)

  // Load translations based on current language
  useEffect(() => {
    const loadTranslations = async () => {
      setIsLoading(true)
      try {
        const translationModules = await Promise.all([
          import(`../locales/${language}/common.${language}.js`),
          import(`../locales/${language}/home.${language}.js`),
          import(`../locales/${language}/about.${language}.js`),
          import(`../locales/${language}/services.${language}.js`),
          import(`../locales/${language}/therapy.${language}.js`),
          import(`../locales/${language}/resources.${language}.js`),
          import(`../locales/${language}/workspace.${language}.js`),
          import(`../locales/${language}/contact.${language}.js`),
          import(`../locales/${language}/careers.${language}.js`).catch(() => null),
          import(`../locales/${language}/sitemap.${language}.js`).catch(() => null),
          import(`../locales/${language}/emdr.${language}.js`),
          import(`../locales/${language}/slidingScale.${language}.js`),
          import(`../locales/${language}/groups.${language}.js`),
          import(`../locales/${language}/modalitiesRates.${language}.js`),
          import(`../locales/${language}/servicesInvestment.${language}.js`),
          import(`../locales/${language}/intake.${language}.js`),
          import(`../locales/${language}/smsLegal.${language}.js`),
          import(`../locales/${language}/locations.${language}.js`)
        ])
        
        const [
          commonModule,
          homeModule,
          aboutModule,
          servicesModule,
          therapyModule,
          resourcesModule,
          workspaceModule,
          contactModule,
          careersModule,
          sitemapModule,
          emdrModule,
          slidingScaleModule,
          groupsModule,
          modalitiesRatesModule,
          servicesInvestmentModule,
          intakeModule,
          smsLegalModule,
          locationsModule
        ] = translationModules
        
        setTranslations({
          common: commonModule.common,
          home: homeModule.home,
          about: aboutModule.about,
          services: servicesModule.services,
          therapy: therapyModule.therapy,
          resources: resourcesModule.resources,
          workspace: workspaceModule.workspace,
          contact: contactModule.contact,
          careers: careersModule?.careers || {},
          sitemap: sitemapModule?.sitemap || {},
          emdr: emdrModule.emdr,
          slidingScale: slidingScaleModule.slidingScale,
          groups: groupsModule.groups,
          modalitiesRates: modalitiesRatesModule.modalitiesRates,
          servicesInvestment: servicesInvestmentModule.servicesInvestment,
          intake: intakeModule.intake,
          smsLegal: smsLegalModule.smsLegal,
          locations: locationsModule.locations
        })
      } catch (error) {
        console.error('Error loading translations:', error)
        // Fallback to English if translation fails
        if (language !== 'en') {
          setLanguage('en')
        }
      } finally {
        setIsLoading(false)
      }
    }

    loadTranslations()
  }, [language])

  // Initialize language from localStorage or browser preference
  useEffect(() => {
    const savedLanguage = localStorage.getItem('viva-language')
    if (savedLanguage && ['en', 'pt'].includes(savedLanguage)) {
      setLanguage(savedLanguage)
    } else {
      // Detect browser language
      const browserLang = navigator.language.split('-')[0]
      if (browserLang === 'pt') {
        setLanguage('pt')
      } else {
        setLanguage('en') // Default to English
      }
    }
  }, [])

  // Save language preference to localStorage
  const changeLanguage = (newLanguage) => {
    if (['en', 'pt'].includes(newLanguage)) {
      setLanguage(newLanguage)
      localStorage.setItem('viva-language', newLanguage)
    }
  }

  /** Sync from /en or /pt URL prefix without navigation (see LanguageRouteSync). */
  const syncLanguageFromPath = useCallback((langFromPath) => {
    if (['en', 'pt'].includes(langFromPath)) {
      setLanguage((current) => {
        if (current === langFromPath) return current
        localStorage.setItem('viva-language', langFromPath)
        return langFromPath
      })
    }
  }, [])

  // Translation function
  const t = (key, fallback = '') => {
    // Handle undefined or null key
    if (!key || typeof key !== 'string') {
      return fallback || 'Translation key missing'
    }
    
    // If translations are not loaded yet, return fallback
    if (isLoading || Object.keys(translations).length === 0) {
      return fallback || key
    }
    
    const keys = key.split('.')
    let value = translations
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k]
      } else {
        // Debug logging for missing translations
        if (process.env.NODE_ENV === 'development') {
          console.warn(`Translation key not found: ${key}`, { availableKeys: Object.keys(translations) })
        }
        return fallback || key
      }
    }
    
    return value !== undefined ? value : fallback || key
  }

  const value = {
    language,
    changeLanguage,
    syncLanguageFromPath,
    t,
    translations,
    isLoading
  }

  // Debug logging in development
  if (process.env.NODE_ENV === 'development') {
    console.log('LanguageContext value:', { 
      language, 
      isLoading, 
      hasTranslations: Object.keys(translations).length > 0,
      translationKeys: Object.keys(translations),
      commonNav: translations.common?.nav
    })
  }

  // Staff apps (HUB / Scheduler) do not need marketing locale bundles before first paint.
  // Blocking them behind the full translation load caused a multi-second blank/shell flash.
  const isStaffApp =
    typeof window !== 'undefined' &&
    (window.location.pathname.startsWith('/hub') ||
      window.location.pathname.startsWith('/scheduler'))

  // Show loading state while translations are being loaded (public site only)
  if (isLoading && !isStaffApp) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary-800 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}
